import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { loginUser, loginWithCustomToken } from "../../services/auth/AuthServices";
import {
  checkLoginMode,
  sendLoginCode,
  verifyLoginCode,
} from "../../services/backend/EntityAuthService";
import { setUser } from "../../redux/slices/userSlice";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import Link from "@mui/material/Link";
import Box from "@mui/material/Box";

//importar hoja de estilos
import "./Login.css";

// Importa MUI components
import {
  Checkbox,
  Container,
  FormControlLabel,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";

import { debugLog } from "../../functions";

// The panel is two-step: the email first, then a password or a code depending on
// who it is.
//
// Internal staff sign in with a password, as always. Company users have no
// password — their Auth account is created without one — and sign in with a code
// sent to their address. The detection happens in the backend because the client
// cannot read the `users` collection without a session.
const STEP = { EMAIL: "email", PASSWORD: "password", CODE: "code" };

const RESEND_SECONDS = 45;

export default function Login() {
  const dispatch = useDispatch();
  const [step, setStep] = useState(STEP.EMAIL);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [keepSesion, setKeepSesion] = useState(true);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  // Typing just the username gets the default domain appended, as before.
  const fullEmail = useMemo(
    () => (email.includes("@")
      ? email.trim()
      : `${email.trim()}@${process.env.REACT_APP_DEFAULT_EMAIL_CLIENT}`),
    [email]
  );

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const finishLogin = useCallback((response) => {
    if (!response || !response.user) {
      setError("No fue posible cargar tu perfil. Intenta de nuevo.");
      setLoading(false);
      return;
    }
    dispatch(setUser(response.user));
    setError("");
    navigate("/home");
    setLoading(false);
  }, [dispatch, navigate]);

  // Step 1. If detection fails, checkLoginMode already returns "password": a
  // problem in that function cannot lock out the existing users.
  const handleEmailStep = async () => {
    setLoading(true);
    setError("");
    const mode = await checkLoginMode(fullEmail);
    if (mode === "otp") {
      const sent = await sendLoginCode(fullEmail);
      if (!sent.ok) {
        setError(sent.error);
        setLoading(false);
        return;
      }
      setInfo(`Te enviamos un código a ${fullEmail}.`);
      setCooldown(RESEND_SECONDS);
      setStep(STEP.CODE);
    } else {
      setStep(STEP.PASSWORD);
    }
    setLoading(false);
  };

  const handlePasswordStep = async () => {
    setLoading(true);
    try {
      const response = await loginUser({
        email: fullEmail,
        password,
        // The service reads `rememberMe`. Until now it was sent `keepSesion`, so
        // the "Mantener Sesión" checkbox did nothing.
        rememberMe: keepSesion,
      });
      finishLogin(response);
    } catch (err) {
      handleFirebaseError(err);
    }
  };

  const handleCodeStep = async () => {
    setLoading(true);
    setError("");
    const verified = await verifyLoginCode(fullEmail, code.trim());
    if (!verified.ok) {
      setError(verified.error);
      setLoading(false);
      return;
    }
    const response = await loginWithCustomToken(verified.customToken, keepSesion);
    finishLogin(response);
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError("");
    const sent = await sendLoginCode(fullEmail);
    if (!sent.ok) {
      setError(sent.error);
      return;
    }
    setInfo(`Te enviamos un código nuevo a ${fullEmail}.`);
    setCooldown(RESEND_SECONDS);
  };

  const handleBack = () => {
    setStep(STEP.EMAIL);
    setPassword("");
    setCode("");
    setError("");
    setInfo("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    debugLog("login step:", step);
    if (step === STEP.EMAIL) return handleEmailStep();
    if (step === STEP.PASSWORD) return handlePasswordStep();
    return handleCodeStep();
  };

  const handleFirebaseError = (error) => {
    let errorMessage;
    console.error(error.code);

    switch (error.code) {
      case "auth/invalid-email":
        errorMessage = "El formato del email es incorrecto.";
        break;
      case "auth/user-disabled":
        errorMessage = "Este usuario ha sido deshabilitado.";
        break;
      case "auth/user-not-found":
        errorMessage = "No existe un usuario con este correo electrónico.";
        break;
      case "auth/wrong-password":
        errorMessage = "La contraseña es incorrecta.";
        break;
      default:
        errorMessage = "Ha ocurrido un error al intentar autenticarse.";
    }
    setError(errorMessage);
    setLoading(false);

    console.error(errorMessage);
  };

  const submitLabel = step === STEP.EMAIL ? "Continuar" : "Ingresar";

  return (
    <div className="container">
      <Container component="div" maxWidth="xs" className="form-container">
        <img
          src={`${process.env.PUBLIC_URL}/${process.env.REACT_APP_LOGOTIPO_LOGIN}`}
          alt="Logo de la App"
          className="app-logo"
        />

        <Typography component="h1" variant="h5">
          Iniciar Sesión
        </Typography>
        {error && (
          <Alert severity="error" className="error-alert">
            {error}
          </Alert>
        )}
        {!error && info && step === STEP.CODE && (
          <Alert severity="info" className="error-alert">
            {info}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <TextField
            variant="outlined"
            margin="normal"
            required
            fullWidth
            id="email"
            label="Correo Electrónico"
            name="email"
            autoFocus
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            // In steps 2 and 3 the address is already settled: it is shown, but
            // changed through "Usar otro correo", which also clears what was
            // typed.
            disabled={step !== STEP.EMAIL}
          />

          {step === STEP.PASSWORD && (
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Contraseña"
              type="password"
              id="password"
              autoFocus
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          )}

          {step === STEP.CODE && (
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="code"
              label="Código de 6 dígitos"
              id="code"
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              inputProps={{ inputMode: "numeric", maxLength: 6 }}
            />
          )}

          <FormControlLabel
            control={
              <Checkbox
                checked={keepSesion}
                size="small"
                onChange={(e) => setKeepSesion(e.target.checked)}
              />
            }
            label="Mantener Sesión"
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : <span>{submitLabel}</span>}
          </Button>
        </form>

        {step === STEP.CODE && (
          <Box mt={2} textAlign="center">
            <Button size="small" onClick={handleResend} disabled={cooldown > 0}>
              {cooldown > 0 ? `Reenviar código (${cooldown}s)` : "Reenviar código"}
            </Button>
          </Box>
        )}

        {step !== STEP.EMAIL && (
          <Box mt={1} textAlign="center">
            <Button size="small" onClick={handleBack}>
              Usar otro correo
            </Button>
          </Box>
        )}

        {step !== STEP.CODE && (
          <Box mt={3} textAlign="center">
            <Link component={RouterLink} to="/password-reset">
              ¿Olvidaste tu contraseña?
            </Link>
          </Box>
        )}
      </Container>
    </div>
  );
}
